/**
 * Moves the venue_static references the older merge helpers in `database_admin.ts` never touched.
 *
 * Before this, a merge left these rows on the old hotel. Some foreign keys have no ON DELETE rule
 * (extractions, the portal link, contracts, booking links and responses), so they blocked the old
 * row's delete and left a half-merged duplicate behind. The rest cascade, so the delete quietly took
 * client preferred and blacklisted hotels, folders, bounces and the like with it.
 *
 * The rules follow the checked-in SQL merge script: every row moves to the surviving hotel; a table
 * that holds one row per hotel keeps the first one and drops the rest; a row that would break a
 * unique rule once moved is dropped from the old hotel's side where that loses nothing (the same
 * folder twice), and stops the merge where it would lose real work (two extractions of one email).
 */
import type { StaticVenue } from "@/lib/database/venues";
import type { SupabaseClient } from "@supabase/supabase-js";

/** How one table's venue_static column is carried over to the surviving hotel. */
export interface VenueReferenceRule {
  table: string;
  column: string;
  /** "move": every row moves. "one_per_venue": the first row stays, the others are dropped.
   *  "drop_clashes": rows that would clash on `keys` are dropped, the rest move. */
  mode: "move" | "one_per_venue" | "drop_clashes";
  /** The other columns of the unique rule that includes `column` (drop_clashes only). */
  keys?: string[];
}

/** Tables the older helpers in `database_admin.ts` already move or rebuild. */
export const VENUE_TABLES_MOVED_ELSEWHERE = [
  "ava_workflows",
  "contract_requests",
  "customer_venue_answers",
  "ema_candidate_threads",
  "enterprise_contract_negotiations",
  "event_venue_bookings",
  "event_venue_candidate",
  "extracted_quote",
  "extracted_quote_modified",
  "invoice_files",
  "oee_quotes",
  "venue_communications",
  "venue_photo_captions",
  "venue_quote",
  "venue_static_accessible",
  "venue_static_geocoded",
  "venue_static_geocoded_google_maps",
  "venue_static_meeting_space",
  "venue_static_meeting_space_detailed",
  "wik_reviews",
  "wik_venues_posts",
] as const;

/** The tables this module carries over, and how. Unique rules read from prod on 2026-11-03. */
export const VENUE_REFERENCE_RULES: VenueReferenceRule[] = [
  // No ON DELETE rule: these blocked the old hotel's delete.
  { table: "oee_extractions", column: "venue_static_id", mode: "move" },
  { table: "venue_venue_static", column: "venue_static_id", mode: "one_per_venue" },
  { table: "enterprise_contracts", column: "venue_static_id", mode: "move" },
  { table: "hotel_booking_links", column: "venue_static_id", mode: "move" },
  { table: "hotel_booking_responses", column: "venue_static_id", mode: "move" },
  // ON DELETE CASCADE: the delete used to take these with it.
  { table: "enterprise_preferred_venues", column: "venue_static_id", mode: "drop_clashes", keys: ["organization_id"] },
  {
    table: "enterprise_blacklisted_venues",
    column: "venue_static_id",
    mode: "drop_clashes",
    keys: ["organization_id"],
  },
  { table: "venue_folder_venue", column: "venue_static_id", mode: "drop_clashes", keys: ["folder_id"] },
  { table: "venue_email_bounces", column: "venue_static_id", mode: "drop_clashes", keys: ["email_normalized"] },
  { table: "fetch_tasks", column: "venue_static_id", mode: "drop_clashes", keys: ["event_id", "task_type"] },
  { table: "client_feed_read", column: "venue_static_id", mode: "drop_clashes", keys: ["user_id", "event_id"] },
  { table: "hotel_brand_match", column: "venue_static_id", mode: "one_per_venue" },
  { table: "venue_static_quo", column: "venue_static_id", mode: "one_per_venue" },
  { table: "venue_image_analysis", column: "venue_static_id", mode: "one_per_venue" },
  { table: "client_feed_entry", column: "venue_static_id", mode: "move" },
  { table: "estimate_log", column: "venue_static_id", mode: "move" },
  { table: "planner_bot_notifications", column: "venue_static_id", mode: "move" },
  { table: "quo_dial_intents", column: "venue_static_id", mode: "move" },
  { table: "quote_requests", column: "venue_static_id", mode: "move" },
  { table: "venue_image_changes", column: "venue_static_id", mode: "move" },
  { table: "venue_photo_uploads", column: "venue_static_id", mode: "move" },
  { table: "venue_static_phone_alias", column: "venue_static_id", mode: "move" },
  { table: "meeting_invite", column: "venue_static_id", mode: "move" },
  // ON DELETE SET NULL: the call would lose its hotel.
  { table: "quo_call_ledger", column: "matched_venue_static_id", mode: "move" },
];

type Row = Record<string, unknown>;

const keyOf = (row: Row, keys: string[]) => JSON.stringify(keys.map((key) => row[key]));
const hasNullKey = (row: Row, keys: string[]) => keys.some((key) => row[key] === null || row[key] === undefined);

/**
 * Refuses a merge, before anything is written, that these rules could not carry over whole: old
 * hotels linked to different portal venues, or two old hotels holding an extraction of the same
 * email (the unique rule would force one to be dropped).
 *
 * @param supabase - A service-role client.
 * @param oldHotels - The hotels being merged away.
 * @returns Why the merge can't run, or null when it can.
 * @throws Never.
 */
export async function findVenueReferenceMergeConflict(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[]
): Promise<string | null> {
  const oldIds = oldHotels.map((hotel) => hotel.venue_static_id);

  const { data: links, error: linkError } = await supabase
    .schema("nowadays")
    .from("venue_venue_static")
    .select("venue_id, venue_static_id")
    .in("venue_static_id", oldIds);
  if (linkError) return `Couldn't check the hotels' portal links before merging: ${linkError.message}`;
  const portalVenues = new Set((links ?? []).map((link: Row) => link.venue_id));
  if (portalVenues.size > 1) {
    return (
      `These hotels are linked to ${portalVenues.size} different portal venues ` +
      `(${[...portalVenues].join(", ")}); merge those first.`
    );
  }

  const { data: extractions, error: extractionError } = await supabase
    .schema("nowadays")
    .from("oee_extractions")
    .select("gmail_message_id, venue_static_id")
    .in("venue_static_id", oldIds);
  if (extractionError) return `Couldn't check the hotels' extractions before merging: ${extractionError.message}`;
  const hotelsByMessage = new Map<unknown, Set<unknown>>();
  for (const row of (extractions ?? []) as Row[]) {
    if (row.gmail_message_id === null || row.gmail_message_id === undefined) continue;
    const hotels = hotelsByMessage.get(row.gmail_message_id) ?? new Set();
    hotels.add(row.venue_static_id);
    hotelsByMessage.set(row.gmail_message_id, hotels);
  }
  const shared = [...hotelsByMessage.entries()].filter(([, hotels]) => hotels.size > 1);
  if (shared.length > 0) {
    return `${shared.length} email(s) were extracted for more than one of these hotels; look at them by hand first.`;
  }
  return null;
}

/**
 * Carries one table over from one old hotel to the surviving hotel.
 *
 * @returns How many rows moved and were dropped, or the error that stopped it.
 */
async function carryOver(
  supabase: SupabaseClient,
  rule: VenueReferenceRule,
  oldId: number,
  newId: number
): Promise<{ moved: number; dropped: number; error: string | null }> {
  const table = () => supabase.schema("nowadays").from(rule.table);
  let dropped = 0;

  if (rule.mode === "one_per_venue") {
    const { data: existing, error } = await table().select(rule.column).eq(rule.column, newId);
    if (error) return { moved: 0, dropped, error: error.message };
    if ((existing ?? []).length > 0) {
      const { error: deleteError, count } = await table().delete({ count: "exact" }).eq(rule.column, oldId);
      if (deleteError) return { moved: 0, dropped, error: deleteError.message };
      return { moved: 0, dropped: count ?? 0, error: null };
    }
  }

  if (rule.mode === "drop_clashes") {
    const keys = rule.keys ?? [];
    const { data: rows, error } = await table()
      .select([rule.column, ...keys].join(", "))
      .in(rule.column, [oldId, newId]);
    if (error) return { moved: 0, dropped, error: error.message };
    const all = (rows ?? []) as unknown as Row[];
    const taken = new Set(all.filter((row) => row[rule.column] === newId).map((row) => keyOf(row, keys)));
    const clashes = all.filter(
      (row) => row[rule.column] === oldId && !hasNullKey(row, keys) && taken.has(keyOf(row, keys))
    );
    for (const clash of clashes) {
      let remove = table().delete({ count: "exact" }).eq(rule.column, oldId);
      for (const key of keys) remove = remove.eq(key, clash[key]);
      const { error: deleteError, count } = await remove;
      if (deleteError) return { moved: 0, dropped, error: deleteError.message };
      dropped += count ?? 0;
    }
  }

  const { error, count } = await table()
    .update({ [rule.column]: newId }, { count: "exact" })
    .eq(rule.column, oldId);
  if (error) return { moved: 0, dropped, error: error.message };
  return { moved: count ?? 0, dropped, error: null };
}

/**
 * Moves every reference in `VENUE_REFERENCE_RULES` from the old hotels to the surviving hotel, so the
 * old rows can be deleted without being blocked and without cascading anything away. Old hotels are
 * taken first-made first, so where only one row can stay, the oldest hotel's row wins.
 *
 * @param supabase - A service-role client.
 * @param oldHotels - The hotels being merged away.
 * @param newHotel - The surviving hotel.
 * @returns success false with the first error; otherwise a per-table count of moved and dropped rows.
 * @throws Never.
 */
export async function updateRemainingVenueReferencesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  const oldIds = oldHotels
    .map((hotel) => hotel.venue_static_id)
    .filter((id) => id !== newHotel.venue_static_id)
    .sort((a, b) => a - b);
  const summary: string[] = [];

  for (const rule of VENUE_REFERENCE_RULES) {
    let moved = 0;
    let dropped = 0;
    for (const oldId of oldIds) {
      const result = await carryOver(supabase, rule, oldId, newHotel.venue_static_id);
      if (result.error) {
        return { success: false, message: `Error moving ${rule.table} from venue ${oldId}: ${result.error}` };
      }
      moved += result.moved;
      dropped += result.dropped;
    }
    if (moved + dropped > 0) summary.push(`${rule.table}: ${moved} moved, ${dropped} dropped`);
  }

  return {
    success: true,
    message: summary.length > 0 ? summary.join("; ") : "No other venue references to move",
  };
}
